import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { ArrowRight, Zap, Scale, TrendingUp, BarChart3, Sparkles, Tag } from "lucide-react";
import QuickSearch from "@/components/QuickSearch";
import AdBanner from "@/components/AdBanner";
import FaqSection from "@/components/landing/FaqSection";

const Landing = () => {
  const { t } = useLang();

  return (
    <main data-testid="landing-page" className="bg-white text-[#0A0A0A]">
      {/* HERO */}
      <section className="relative border-b border-black overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-12 sm:pt-16 pb-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="overline text-[#002FA7]">{t.landing.kicker}</span>
              <span className="w-12 h-px bg-black/20" />
              <span className="overline">v.1.0</span>
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-tighter">
              {t.landing.h1_part1}<br />
              <span className="inline-block">
                <span className="bg-[#FFCC00] px-2">{t.landing.h1_part2}</span>
                <span className="cursor-blink text-[#FF3B30]">_</span>
              </span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl max-w-2xl text-[#4A4A4A] leading-relaxed">
              {t.landing.sub}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/valutazione" data-testid="hero-cta-primary" className="btn-primary">
                {t.landing.cta_primary} <ArrowRight size={18} />
              </Link>
              <a href="#how" data-testid="hero-cta-secondary" className="btn-outline">{t.landing.cta_secondary}</a>
            </div>
          </div>

          {/* Quick autocomplete search — primary conversion hook */}
          <div className="lg:col-span-4">
            <QuickSearch />
          </div>
        </div>

        {/* Marquee */}
        <div className="border-t border-black bg-[#0A0A0A] text-white overflow-hidden">
          <div className="flex marquee-track whitespace-nowrap py-3">
            {["marquee-a", "marquee-b"].map((id) => (
              <div key={id} className="flex items-center gap-12 px-8 font-mono text-sm font-bold tracking-widest">
                <span>VINTED</span><span>·</span>
                <span>EBAY</span><span>·</span>
                <span>SUBITO</span><span>·</span>
                <span>WALLAPOP</span><span>·</span>
                <span>FB MARKETPLACE</span><span>·</span>
                <span className="text-[#FFCC00]">★ CLAUDE SONNET 4.5 INSIDE</span><span>·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-black">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3">
          {[
            { n: t.landing.stat1_n, l: t.landing.stat1_l },
            { n: t.landing.stat2_n, l: t.landing.stat2_l },
            { n: t.landing.stat3_n, l: t.landing.stat3_l },
          ].map((s, i) => (
            <div key={s.l} className={`p-8 lg:p-12 ${i !== 2 ? "md:border-r border-black" : ""} border-b md:border-b-0 border-black`}>
              <div className="font-display font-black text-6xl sm:text-7xl lg:text-8xl leading-none tracking-tighter">{s.n}</div>
              <div className="overline mt-3 text-[#737373]">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* AD SLOT */}
      <section className="ad-section border-b border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <AdBanner slot="landing-top" />
        </div>
      </section>

      {/* HOW / STRATEGIES */}
      <section id="how" className="border-b border-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl">
            <span className="overline text-[#002FA7]">01 / STRATEGIES</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none mt-4">
              {t.landing.how_title}
            </h2>
            <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">{t.landing.how_sub}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-14 border border-black">
            {/* FAST */}
            <div className="p-8 border-b md:border-b-0 md:border-r border-black bg-white relative group hover:bg-[#F5F5F5] transition-colors">
              <div className="flex items-center justify-between">
                <Zap size={28} className="text-[#002FA7]" />
                <span className="overline text-[#002FA7]">01 / FAST</span>
              </div>
              <div className="font-display font-black text-3xl tracking-tighter mt-8">{t.landing.strat_fast}</div>
              <p className="mt-3 text-[#4A4A4A]">{t.landing.strat_fast_desc}</p>
            </div>
            {/* BALANCED */}
            <div className="p-8 border-b md:border-b-0 md:border-r border-black bg-[#FFCC00]/10 relative">
              <div className="flex items-center justify-between">
                <Scale size={28} className="text-[#0A0A0A]" />
                <span className="overline">02 / BALANCED</span>
              </div>
              <div className="font-display font-black text-3xl tracking-tighter mt-8">{t.landing.strat_balanced}</div>
              <p className="mt-3 text-[#4A4A4A]">{t.landing.strat_balanced_desc}</p>
            </div>
            {/* MAX */}
            <div className="p-8 bg-white relative group hover:bg-[#F5F5F5] transition-colors">
              <div className="flex items-center justify-between">
                <TrendingUp size={28} className="text-[#FF3B30]" />
                <span className="overline text-[#FF3B30]">03 / MAX</span>
              </div>
              <div className="font-display font-black text-3xl tracking-tighter mt-8">{t.landing.strat_max}</div>
              <p className="mt-3 text-[#4A4A4A]">{t.landing.strat_max_desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="border-b border-black bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <span className="overline text-[#002FA7]">02 / PROCESS</span>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none mt-4">{t.landing.steps_title}</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {[
              { n: "01", icon: <Tag />, title: t.landing.step1_t, desc: t.landing.step1_d },
              { n: "02", icon: <BarChart3 />, title: t.landing.step2_t, desc: t.landing.step2_d },
              { n: "03", icon: <Sparkles />, title: t.landing.step3_t, desc: t.landing.step3_d },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-black p-8 hover:-translate-y-1 hover:shadow-hard-sm transition-all">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-display font-black text-5xl tracking-tighter">{s.n}</span>
                  <span className="text-[#002FA7]">{s.icon}</span>
                </div>
                <div className="font-display font-black text-2xl tracking-tight">{s.title}</div>
                <p className="mt-2 text-[#4A4A4A]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AD SLOT — between "Come funziona" text block and FAQ */}
      <section className="ad-section border-b border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <AdBanner slot="landing-bottom" />
        </div>
      </section>

      {/* FAQ */}
      <FaqSection />

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none">{t.landing.cta_bottom}</h2>
            <p className="mt-4 text-[#A0A0A0] text-lg">{t.landing.footer_tag}</p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/valutazione" data-testid="footer-cta" className="inline-flex items-center gap-2 bg-[#FFCC00] text-black px-8 py-4 font-display font-black tracking-tight hover:bg-white transition-colors">
              {t.landing.cta_bottom_btn} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Landing;
